import { type FC } from 'react';

import { AppRole, OrganizationType } from 'connect-graphql/generated';

import { AdminNavLinks } from '../AdminNavLinks';
import { CraftAdminNavLinks } from '../CraftAdminNavLinks';
import { EducationProviderAdminNavLinks } from '../EducationProviderAdminNavLinks';
import { EmployerAdminNavLinks } from '../EmployerAdminNavLinks';
import { EvaluatorNavLinks } from '../EvaluatorNavLinks';
import { FacilitatorNavLinks } from '../FacilitatorNavLinks';
import { InstructorNavLinks } from '../InstructorNavLinks';
import { LearnerNavLinks } from '../LearnerNavLinks';
import { ObserverNavLinks } from '../ObserverNavLinks';
import { SponsorAdminNavLinks } from '../SponsorAdminNavLinks';

const OrganizationTypeNavLinks: Record<OrganizationType, FC> = {
  [OrganizationType.EducationProvider]: EducationProviderAdminNavLinks,
  [OrganizationType.Employer]: EmployerAdminNavLinks,
  [OrganizationType.Sponsor]: SponsorAdminNavLinks,
  [OrganizationType.SystemAdmin]: CraftAdminNavLinks,
};

/**
 * Depending on the organization type, this will return the appropriate nav links for the admin.
 * If no organization type is provided, it will return the generic admin nav links.
 *
 * @param {OrganizationType} organizationType
 *   If provided, will return the nav links for the admin's organization type.
 *
 * @returns {FC<NavLinkListProps>} The nav links for the admin based on organization type.
 */
const getAdminNavLinks = (organizationType?: OrganizationType): FC => {
  if (!organizationType) {
    return AdminNavLinks;
  }
  return OrganizationTypeNavLinks[organizationType];
};

const RoleNavLinks: Record<
  AppRole,
  (params: { readonly organizationType?: OrganizationType }) => FC
> = {
  [AppRole.Admin]: ({ organizationType }) => getAdminNavLinks(organizationType),
  [AppRole.Evaluator]: () => EvaluatorNavLinks,
  [AppRole.Facilitator]: () => FacilitatorNavLinks,
  [AppRole.Instructor]: () => InstructorNavLinks,
  [AppRole.Learner]: () => LearnerNavLinks,
  [AppRole.Observer]: () => ObserverNavLinks,
};

/**
 * Depending on the role, this will return the appropriate nav links for the user. Additionally if
 * the user is an admin, it will return the appropriate nav links based on the organization type.
 *
 * @param {AppRole} role The role of the user.
 * @param {OrganizationType} organizationType
 *   The organization type of the user. Used to determine the nav links for the admin.
 *
 * @returns {FC<NavLinkListProps>}
 *   The nav links for the user based on their role and if applicable, their organization type.
 */
export const getRoleNavLinks = (role: AppRole, organizationType?: OrganizationType): FC | null =>
  RoleNavLinks[role]({ organizationType });
